import { DateTime, Interval } from "luxon";

export const isToday = (d: DateTime | string) => {
  const date = typeof d === "string" ? DateTime.fromISO(d) : d;
  return date.hasSame(DateTime.local(), "day");
};

export const isThisYear = (date: DateTime) => {
  return date.hasSame(DateTime.local(), "year");
};

export const generateShortDateString = (d: DateTime | string) => {
  const date = typeof d === "string" ? DateTime.fromISO(d) : d;
  if (!date.isValid) {
    return "";
  }
  if (isToday(date)) {
    return date.toFormat("h:mma").toLowerCase();
  }
  if (isThisYear(date)) {
    return date.toFormat("MMM d");
  }
  return date.toFormat("M/d/yy");
};

export const formatDateString = (d: DateTime | string, format: string) => {
  const date = typeof d === "string" ? DateTime.fromISO(d) : d;
  if (!date.isValid) {
    return "";
  }
  return date.toFormat(format);
};

export const generateLongDateString = (d: DateTime | string): string => {
  const date = typeof d === "string" ? DateTime.fromISO(d) : d;
  if (!date.isValid) return "";
  return date.toFormat("MMMM d, yyyy");
};

export const generateShortTimeString = (d: DateTime | string): string => {
  const date = typeof d === "string" ? DateTime.fromISO(d) : d;
  if (!date.isValid) return "";
  return date.toFormat("h:mm a");
};

export const generateLongDateAtTimeString = (d: DateTime | string): string => {
  const date = typeof d === "string" ? DateTime.fromISO(d) : d;
  if (!date.isValid) return "";
  return date.toFormat("MMMM d, yyyy 'at' h:mma").replace(/(AM|PM)$/, m => m.toLowerCase());
};

type TGenerateRecentDateStringOptions = {
  skipFullDayName?: boolean;
  relativeDateShouldBeLowercase?: boolean;
};

export const generateRecentDateString = (
  v: DateTime | string,
  { skipFullDayName, relativeDateShouldBeLowercase }: TGenerateRecentDateStringOptions = {
    skipFullDayName: false,
    relativeDateShouldBeLowercase: false,
  }
) => {
  const date = typeof v === "string" ? DateTime.fromISO(v) : v;
  if (!date.isValid) {
    return "";
  }

  const now = DateTime.local().endOf("day");
  const dateDiff = Interval.fromDateTimes(date, now).length("days");

  let dateString = "";
  if (dateDiff < 1) {
    dateString = "Today";
  } else if (dateDiff < 2) {
    dateString = "Yesterday";
  } else if (dateDiff < 7 && !skipFullDayName) {
    dateString = date.toFormat("cccc"); // Full day name
  } else if (date.hasSame(now, "year")) {
    dateString = date.toFormat("MMM d");
  } else {
    dateString = date.toFormat("M/d/yy");
  }

  if (relativeDateShouldBeLowercase && (dateString === "Today" || dateString === "Yesterday")) {
    return dateString.charAt(0).toLowerCase() + dateString.slice(1);
  }

  return dateString.charAt(0).toUpperCase() + dateString.slice(1);
};

export const generateRelativePrepositionAdverbialDateString = (date: DateTime | string) => {
  const recentDateString = generateRecentDateString(date);
  return isToday(date)
    ? `today at ${generateShortDateString(date)}`
    : recentDateString === "Yesterday"
      ? "yesterday"
      : `on ${recentDateString}`;
};

type TGenerateRelativeDateTimeOptions = {
  /**
   * When true (default), older dates are prefixed with "on" so the result
   * reads as a sentence fragment ("on Friday at 3:38 PM"). Set to false for
   * label-style metadata where no preposition is wanted ("Friday at 3:38 PM").
   * The today/yesterday branches always render as adverbs ("today" /
   * "yesterday") regardless of this flag.
   */
  withPreposition?: boolean;
};

/**
 * Relative date phrase + time of day. The time is always present and
 * uniformly appended.
 *
 * With preposition (default — sentence-fragment style):
 *   today    → "today at 3:38 PM"
 *   yesterday → "yesterday at 3:38 PM"
 *   < 7 days → "on Friday at 3:38 PM"
 *   this yr  → "on May 17 at 3:38 PM"
 *   other yr → "on 5/17/22 at 3:38 PM"
 *   invalid  → ""
 *
 * Without preposition (label style):
 *   < 7 days → "Friday at 3:38 PM"
 *   this yr  → "May 17 at 3:38 PM"
 *   other yr → "5/17/22 at 3:38 PM"
 */
export const generateRelativePrepositionAdverbialDateTimeString = (
  d: DateTime | string,
  { withPreposition = true }: TGenerateRelativeDateTimeOptions = {}
): string => {
  const date = typeof d === "string" ? DateTime.fromISO(d) : d;
  if (!date.isValid) return "";

  const recent = generateRecentDateString(date);
  const time = generateShortTimeString(date);

  let datePhrase: string;
  if (recent === "Today") {
    datePhrase = "today";
  } else if (recent === "Yesterday") {
    datePhrase = "yesterday";
  } else {
    // generateRecentDateString returns "M/d/yy" for different-year dates;
    // swap that compact form for the long form ("March 1, 2025").
    const dateLabel = /^\d/.test(recent) ? generateLongDateString(date) : recent;
    datePhrase = withPreposition ? `on ${dateLabel}` : dateLabel;
  }

  return `${datePhrase} at ${time}`;
};

export const ZERO_DATE_STRING = "0000-00-00T00:00:00.000Z";

type TGenerateVersionDateOptions = {
  separator: "dot" | "comma";
  abbreviateMonthWithYear?: boolean;
  compact?: boolean;
};

export const generateVersionDate = (
  date: DateTime,
  {
    separator,
    abbreviateMonthWithYear: abbreviateMonthWhenYear = false,
    compact = false,
  }: TGenerateVersionDateOptions
) => {
  if (!date.isValid) {
    return "";
  }
  const isCurrentYear = date.hasSame(DateTime.local(), "year");
  const monthToken = !isCurrentYear && abbreviateMonthWhenYear ? "LLL" : "LLLL";

  if (compact) {
    // Compact representation for tight spaces
    // Today: just time; current year: M/d h:mma; other years: M/d/yy h:mma
    if (isToday(date)) {
      return date.toFormat("h:mma").toLowerCase();
    }
    const compactDate = isCurrentYear ? date.toFormat("M/d") : date.toFormat("M/d/yy");
    const compactTime = date.toFormat("h:mma").toLowerCase();
    return `${compactDate} ${compactTime}`;
  }

  const datePart = isCurrentYear ? date.toFormat("LLLL d") : date.toFormat(`${monthToken} d, yyyy`);
  // Lowercase am/pm, no leading zero on hour
  const timePart = date.toFormat("h:mma").toLowerCase();

  switch (separator) {
    case "dot":
      return `${datePart} · ${timePart}`;
    case "comma":
      return `${datePart}, ${timePart}`;
  }
};
